import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui";

import { ErrorSurface } from "./ErrorSurface";

/** How long the check breathes before it reports back. */
const CHECK_MS = 900;
/** How long "still no connection" stays before the CTA returns to its resting label. */
const STILL_OFFLINE_MS = 1800;

/**
 * Why this surface renders is not the same question as whether she has a network,
 * and conflating the two is the T535 defect.
 *
 * - `unknown`     — pre-hydration. The server cannot know, so it must not guess:
 *                   this is the state the founder photographed on 2026-08-22, and
 *                   the copy behind it names no cause at all.
 * - `offline`     — the device reports no network. Her connection, her fix.
 * - `unreachable` — the device reports a network and the navigation still failed.
 *                   The LaLiga matchday IP blocks (T507-T513) are the common case
 *                   in Spain; a dead origin is the other. Neither is hers to fix.
 * - `restored`    — she was offline and the network came back. Retry is now worth a tap.
 */
type Reach = "unknown" | "offline" | "unreachable" | "restored";

const EYEBROW: Record<Reach, string> = {
  unknown: "offline.eyebrowUnknown",
  offline: "offline.eyebrow",
  unreachable: "offline.eyebrowUnreachable",
  restored: "offline.eyebrowOnline",
};

const SUB: Record<Reach, string> = {
  unknown: "offline.sub",
  offline: "offline.subOffline",
  unreachable: "offline.subUnreachable",
  restored: "offline.subRestored",
};

/**
 * One mark per state, in the house register — Cormorant italic through `.es-glyph`,
 * the same atom the 404 and the 500 spend their `.af-empty-mark` on.
 *
 * The four SVGs this replaces failed the rubric's criterio 7 on 2026-08-23: the mark is
 * this surface's hero, and three of them were icon-set geometry (`wifi-off` was Lucide
 * path data verbatim, inherited from T104; the block mark was `circle-minus` rebuilt by
 * hand, and at 22px it read as *minus*, not as *shut*). Re-typing an icon's coordinates
 * does not change what the eye receives.
 *
 * The founder drew these herself. They come out of the brand's own type case, so no icon
 * library ships them and the ink weight is even across the four by construction — which
 * was the third fix the reviewer asked for, absorbed rather than applied.
 *
 * Decorative, not explanatory: `ErrorSurface` renders the mark `aria-hidden`, so the
 * meaning is carried by the eyebrow and the sub. These only have to tell the four states
 * apart and set the tone.
 */
const MARK: Record<Reach, string> = {
  /** The open question — the server does not know the cause and does not name one. */
  unknown: "?",
  /** The line, cut. Broken in two it can no longer be read as a minus sign. */
  offline: "\u2013\u00A0\u2013",
  /** The section sign: a court order, not a fault of ours. */
  unreachable: "\u00A7",
  /** The good news, out of the same drawer as the question. */
  restored: "!",
};

/**
 * Offline — the user has no connectivity and this route was not cached.
 *
 * **Boundary with the transient offline banner (design contract):** this
 * fullscreen surface appears only when there is *no* cached content for the
 * route. The lightweight banner that floats over content the user *does* have
 * cached is a different component and is not this one's concern.
 *
 * **No secondary CTA — do not restore one without checking
 * `isCacheableDocument()` first (T517).** There used to be a "see what I have
 * downloaded" link to the library. The library is `cacheStrategy: 'none'`, so
 * offline the navigation failed and the SW's catch handler re-served *this*
 * document: tapping the escape hatch returned the member to the room. Only
 * public documents survive offline, and sending someone mid-training to the
 * sales landing is worse than sending her nowhere.
 *
 * The trap this paragraph was written for: the delivered design used to show
 * that link and assert the view was already SW-cached, which is what persuaded
 * the first implementer. T518 removed that argument from the artifact; T529
 * then removed its other phantom claims. But the reason outlives both
 * corrections, and it is the reason that matters: every private
 * route is `none` (T52), because this app is server-rendered and a private
 * route's document already contains the member's data. That will not change.
 */
export function ErrorPageOffline({ retryHref = null }: {
  /**
   * T841 — the URL she asked for when this page stands in for it (`?from=`). Retry goes
   * there, replacing this entry so Back does not return her to the offline page. Without
   * it, retry reloads.
   */
  retryHref?: string | null;
} = {}) {
  const { t } = useTranslation("error");

  const [reach, setReach] = useState<Reach>("unknown");
  const [pending, setPending] = useState(false);
  const [stillOffline, setStillOffline] = useState(false);

  // Live connectivity. Seeded in an effect rather than from `navigator.onLine`
  // at render time so the server and the first client paint agree.
  useEffect(() => {
    // The seed is the whole diagnosis: this document only ever renders because a
    // navigation failed. If the device still claims a network at that moment, the
    // break is between her ISP and us — not at her end.
    setReach(window.navigator.onLine ? "unreachable" : "offline");

    // Only a real outage can be *recovered from*. Arriving online was never an
    // outage, so an `online` event there means nothing changed and the block-window
    // reading must survive it (R-T535-3).
    const goOnline = () =>
      setReach((prev) => (prev === "offline" ? "restored" : prev));
    const goOffline = () => setReach("offline");

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  function onRetry() {
    if (pending) return;
    setPending(true);
    setStillOffline(false);
    // Never a blind reload — check first, so a failed retry costs nothing.
    window.setTimeout(() => {
      if (window.navigator.onLine) {
        if (retryHref) window.location.replace(retryHref);
        else window.location.reload();
        return;
      }
      setPending(false);
      setStillOffline(true);
      window.setTimeout(() => setStillOffline(false), STILL_OFFLINE_MS);
    }, CHECK_MS);
  }

  const eyebrowKey = EYEBROW[reach];
  const subKey = SUB[reach];

  const primaryLabel = pending
    ? t("offline.primaryPending")
    : stillOffline
      ? t("offline.primaryStillOffline")
      : t("offline.primary");

  return (
    <ErrorSurface
      mark={<span className="es-glyph">{MARK[reach]}</span>}
      eyebrow={
        <div className="af-eyebrow" id="connStatus">
          {t(eyebrowKey)}
        </div>
      }
      title={t("offline.title")}
      sub={t(subKey)}
      primary={
        <Button
          variant="dark"
          className="w-full"
          isLoading={pending}
          onClick={onRetry}
        >
          <span>{primaryLabel}</span>
          {(
            <span className="af-submit-arrow" aria-hidden="true">
              →
            </span>
          )}
        </Button>
      }
      footer={
        <div className="row">
          <span>{t("offline.footer")}</span>
        </div>
      }
    />
  );
}
